import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import { type ComponentPropsWithoutRef, type ReactNode } from 'react';

import IconX from '~icons/bx/x';

import {
	contrastingTextColor,
	nameToHash,
	shouldTextBeLight,
	su,
	theme,
} from '@adflow/frontend/ui-theme';

import { HighlightText } from './HighlightText';
import { HubTooltip } from './HubTooltip';
import { LineClamp } from './LineClamp';

export type TagSize = 'tiny' | 'clickable' | 'medium';

function tagStyles({
	tagColor,
	size,
	disabled,
	global,
}: {
	tagColor: string;
	size: TagSize;
	disabled: boolean;
	global?: boolean;
}) {
	return css(
		global ? su.roundedMd : su.roundedFull,
		{
			display: 'inline-flex',
			alignItems: 'center',
			justifyContent: 'flex-start',
			maxWidth: '100%',
		},
		{
			tiny: css(global ? su.roundedSm : su.roundedFull, su.textXs, {
				paddingLeft: '0.5rem',
				paddingRight: '0.5rem',
				minWidth: '1rem',

				// Make tiny tags centered
				paddingBottom: '0.5px',
			}),
			medium: css(global ? su.roundedMd : su.roundedFull, su.textSm, {
				height: '1.5rem',
				paddingLeft: '0.75rem',
				paddingRight: '0.75rem',
				minWidth: '2rem',
			}),
			clickable: css(su.textMd, {
				height: '2rem',
				paddingLeft: '0.75rem',
				paddingRight: '0.75rem',
				minWidth: '2rem',
			}),
		}[size],
		disabled &&
			css({
				backgroundColor: theme.colors.input.disabledBackground,
				borderColor: theme.colors.input.disabledBorder,
				borderWidth: 1,
			}),
		tagColor === '#FFFFFF' &&
			css({
				borderColor: theme.colors.text.secondary,
				borderWidth: '1px',
			})
	);
}

export interface TagProps extends ComponentPropsWithoutRef<'div'> {
	text: string;
	textStyles?: CSSInterpolation;
	color?: string;
	size?: TagSize;
	searchTerm?: string;
	disabled?: boolean;
	readOnly?: boolean;
	callout?: ReactNode;
	global?: boolean;

	/** When true, show an X icon to indicate that clicking this tag will remove it. */
	removable?: boolean;
}

export function Tag({
	text,
	textStyles,
	color = nameToHash(text ?? ''),
	size = 'tiny',
	removable = false,
	searchTerm,
	disabled = false,
	readOnly = false,
	callout,
	global = false,
	...restProps
}: TagProps) {
	const tag = (
		<div
			css={tagStyles({ tagColor: color, size, disabled, global })}
			{...restProps}
			// Tag color is set on `style` so emotion doesn't create
			// a new class for every rendered tag color
			style={{
				backgroundColor: color,
				color: contrastingTextColor(color),
			}}
		>
			<LineClamp css={textStyles} lines={1}>
				<HighlightText
					text={text}
					highlight={searchTerm}
					highlightColor={shouldTextBeLight(color) ? '#8d8633' : undefined}
					lineClamp
				/>
			</LineClamp>
			{removable && !readOnly ? (
				<div
					css={
						// Note: We assume that this icon is never shown when size != 'clickable'
						{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							width: '2rem',
							height: '2rem',
							marginLeft: '-0.25rem',
							marginRight: '-0.75rem',
						}
					}
				>
					<IconX css={{ verticalAlign: '-0.125em' }} width={20} height={20} />
				</div>
			) : null}
		</div>
	);

	if (callout) {
		return <HubTooltip title={callout}>{tag}</HubTooltip>;
	}

	return tag;
}
